import { useState, useCallback } from 'react'
import { contactAPI } from '../services/api'
import { toast } from '../services/toastService'
import { storage } from '../utils/storage'
import { createLogger } from '../utils/logger'

const logger = createLogger('ContactFormSubmission')

/**
 * useContactFormSubmission - Handles contact form submission logic
 * 
 * @atomic - Single responsibility: contact form submission
 * @idempotent - Same inputs produce same outputs
 * @modular - Self-contained contact form logic
 * 
 * @param {Function} t - Translation function
 * @returns {Object} Contact form state and handlers
 */
export function useContactFormSubmission(t) {
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    entityId: '',
    entityName: '',
    vendorIntent: ''
  })
  const [isVendor, setIsVendor] = useState(false)
  const [contactSuccess, setContactSuccess] = useState(false)
  const [contactRateLimitTime, setContactRateLimitTime] = useState(null)
  const [sending, setSending] = useState(false)

  // Handle contact form field changes
  const handleContactChange = useCallback((e) => {
    const { name, value } = e.target
    setContactFormData(prev => ({ ...prev, [name]: value }))
  }, [])

  // Submit contact form for non-authenticated users
  const handleContactSubmit = useCallback(async (e) => {
    e.preventDefault()
    
    // Check rate limit
    if (contactRateLimitTime && Date.now() < contactRateLimitTime) {
      const remainingTime = Math.ceil((contactRateLimitTime - Date.now()) / 1000)
      toast.error(t('channels.contactForm.rateLimited', { seconds: remainingTime }))
      return
    }

    // Validate required fields
    if (!contactFormData.name || !contactFormData.email || !contactFormData.message) {
      toast.error(t('channels.contactForm.fillRequired'))
      return
    }

    setSending(true)
    try {
      await contactAPI.submitContact({
        name: contactFormData.name,
        email: contactFormData.email,
        subject: contactFormData.subject,
        message: contactFormData.message,
        isVendor: isVendor,
        entityId: contactFormData.entityId || null,
        entityName: contactFormData.entityName || null,
        vendorIntent: contactFormData.vendorIntent || null
      })
      
      setContactSuccess(true)
      setContactRateLimitTime(Date.now() + 60000) // 1 minute rate limit
      
      // Store last submission time
      storage.set('lastContactSubmission', Date.now().toString())
      
      // Reset form
      setContactFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        entityId: '',
        entityName: '',
        vendorIntent: ''
      })
      setIsVendor(false)
      
      toast.success(t('channels.contactForm.messageSent'))
    } catch (error) {
      logger.error('Failed to send contact message', error)
      toast.error(t('channels.contactForm.sendError'))
    } finally {
      setSending(false)
    }
  }, [contactFormData, isVendor, contactRateLimitTime, t])

  // Check for existing rate limit on mount
  const checkExistingRateLimit = useCallback(() => {
    const lastSubmission = storage.get('lastContactSubmission')
    if (lastSubmission) {
      const lastTime = parseInt(lastSubmission)
      const now = Date.now()
      if (now - lastTime < 60000) {
        setContactRateLimitTime(lastTime + 60000)
      }
    }
  }, [])

  return {
    // State
    contactFormData,
    isVendor,
    contactSuccess,
    contactRateLimitTime,
    sending,
    
    // Setters
    setContactFormData,
    setIsVendor,
    setContactSuccess,
    setContactRateLimitTime,
    setSending,
    
    // Handlers
    handleContactChange,
    handleContactSubmit,
    checkExistingRateLimit
  }
}
